import queryString from 'querystringify';

export const MOCK_TYPE = {
    REST: 'REST',
    GRAPHQL: 'GRAPHQL',
};

/**
 * Adapter state allows various requests to share "state" incase on request should impact another.
 *
 * This is useful when a test is making dynamic changes that need to be reflected in downstream requests.
 */
class AdapterState {
    constructor() {
        this._state = {};
    }

    set(key, value) {
        this._state[key] = value;
    }

    get(key) {
        return this._state[key] || {};
    }
}
const adapterState = new AdapterState();

/**
 * This is used to keep track of what fetch calls were made in the browser.
 * We assign getRequestLog to the window object so that webdriver can access it.
 */
const requestLog = [];
window.getRequestLog = () => requestLog;

/**
 * Gets a map of mocks for each service call based on the testCase passed in the URL.
 *
 * Based on the mock type, we mock it based on the GraphQL operation name or the REST HTTP verb.
 * This allows us to mock individual GraphQL requests without having to parse the GraphQL query itself and to mock
 * different REST HTTP verbs behind the same URL.
 */
const registerFetchAdapter = (options) => {
    const testCasesFolderPath = options.testCasesFolderPath || './TestCases/';
    const ignoreUrls = options.ignoreUrls || [];
    const testCase = queryString.parse(window.location.search).testCase;
    let mockMap = {};
    try {
        mockMap = require(`${testCasesFolderPath}${testCase}.js`).default;
    } catch {
        if (!testCase) {
            throw new Error(
                `Test case not found in url. Provide the name of a valid test case in the testCase url param. E.g. /gb/member-profile-2?testCase=`
            );
        }
        throw new Error(`Test case "${testCase}" not found at path "${testCasesFolderPath}${testCase}.js".`);
    }

    const fetch = window.fetch;
    window.fetch = async (url, options) => {
        const realUrl = url.split('?')[0];

        requestLog.push({ url: realUrl, options });

        // If we find any partial match of URLs we should ignore, do a normal fetch
        if (ignoreUrls.some((ignoreUrl) => url.indexOf(ignoreUrl) > -1)) {
            return fetch.apply(window, [url, options]);
        }

        let mock = mockMap[realUrl];

        if (!mock) {
            console.warn(
                `registerFetchAdapter: Request not mocked for url "${url}" because no matching mock was found. Either mock it or add it to "options.ignoreUrls".`
            );
            return fetch.apply(window, [url, options]);
        }

        let operationName = '';
        if (mock.type === MOCK_TYPE.REST) {
            operationName = (options.method || 'GET').toUpperCase();
        } else if (mock.type === MOCK_TYPE.GRAPHQL) {
            operationName = JSON.parse(options.body).operationName;
        }
        const operation = mock.operations[operationName];

        if (!operation) {
            console.warn(
                `registerFetchAdapter: Request not mocked for url "${url}", operation "${operationName}" because no` +
                    ` matching mock was found for the operation.`
            );
            return fetch.apply(window, [url, options]);
        }

        // Lifecycle Hook: Run code before response is calculated. This is useful for creating adapter state.
        operation.onBeforeResponse && operation.onBeforeResponse({ url: realUrl, options, adapterState });

        let response = operation.response;

        // Lifecycle Hook: Run code after response is calculated. This is useful for modifying the response with dynamic data.
        if (operation.onAfterResponse) {
            response = operation.onAfterResponse({ url: realUrl, options, response, adapterState });
        }

        return {
            status: operation.response.status,
            json: () => Promise.resolve(response.body),
        };
    };
};

export default registerFetchAdapter;
